import { useContext, useEffect, useRef, useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import i18next from "i18next";
import AuthContext from "../../context/auth/AuthContext";
import {
  isSkorSdkMode,
  isSdkLangExplicit,
  markSdkLangExplicit,
} from "../../helper/skorSdk";
import {
  LanguageSwitch,
  ProgressRail,
  StepList,
} from "../../components/design";

const OnboardingProgress = ({
  step,
  identityStage,
  skipFinancialStudy,
  companyLogo,
  identityStillValid = false,
  isCompany = false,
  isCompanyTenant = false,
  isIframe = false,
  hideLanguageSwitcher = false,
}) => {
  const { _t } = useTranslator();
  const { changeUserLng, user } = useContext(AuthContext);
  const [currentLang, setCurrentLang] = useState(() =>
    isSkorSdkMode() && !isSdkLangExplicit()
      ? "es"
      : localStorage.getItem("i18nextLng")?.toLowerCase() || "es"
  );
  const [isLangChanging, setIsLangChanging] = useState(false);
  const isChangingLang = useRef(false);

  useEffect(() => {
    // SDK iframe opens in Spanish by default: don't let a backend user.lang
    // flip it to English unless the tenant explicitly switched in this tab.
    if (isSkorSdkMode() && !isSdkLangExplicit()) return;
    if (user?.lang && user.lang.toLowerCase() !== currentLang) {
      // sync if backend lang differs and not currently changing
      if (!isChangingLang.current) {
        const normalized = user.lang.toLowerCase();
        setCurrentLang(normalized);
        i18next.changeLanguage(normalized);
      }
    }
  }, [user?.lang, currentLang]);

  useEffect(() => {
    // The switcher only displays currentLang, so enforce the actual i18n
    // language here too: entering in SDK mode forces Spanish (unless the
    // tenant explicitly chose), and any change made through another
    // switcher on screen is mirrored back into this state.
    if (isSkorSdkMode() && !isSdkLangExplicit()) {
      i18next.changeLanguage("es");
      setCurrentLang("es");
    }
    const syncFromI18n = (lng) => setCurrentLang(lng.toLowerCase());
    i18next.on("languageChanged", syncFromI18n);
    return () => i18next.off("languageChanged", syncFromI18n);
  }, []);

  const handleLanguageChange = async (langVal) => {
    if (isChangingLang.current || isLangChanging) return;
    isChangingLang.current = true;
    setIsLangChanging(true);
    try {
      await i18next.changeLanguage(langVal);
      if (isSkorSdkMode()) markSdkLangExplicit();
      try {
        await changeUserLng?.({ skipUserLoad: true });
      } catch {}
      setCurrentLang(langVal.toLowerCase());
    } finally {
      setTimeout(() => {
        isChangingLang.current = false;
        setIsLangChanging(false);
      }, 1500);
    }
  };

  useEffect(() => {
    document.body.classList.add("tenant-onboarding-progress-visible");
    return () =>
      document.body.classList.remove("tenant-onboarding-progress-visible");
  }, []);

  // SDK embed: URL param (?app_type=skor-sdk) or persisted flag. isIframe prop
  // uses a legacy localStorage check, so OR with the helper to cover first
  // load via URL and expiry handling. Logo only — step logic keeps isIframe.
  const inSdkMode = isIframe || isSkorSdkMode();

  const tenantSteps = [
    {
      id: "details",
      title: _t("yourDetails", "Your details"),
    },
    {
      id: "identity",
      title: _t("identity", "Identity"),
      activeSubstep: identityStage,
      substeps: [
        { id: "document", title: _t("document", "Document") },
        { id: "photos", title: _t("photos", "Photos") },
        { id: "processing", title: _t("processing", "Processing") },
      ],
      ...(identityStillValid ? { badge: _t("stillValid", "Still valid") } : {}),
    },
    {
      id: "documents",
      title: _t("documents", "Documents"),
      activeSubstep:
        step === 3 ? "upload" : step === 4 ? "review" : "processing",
      substeps: [
        { id: "upload", title: _t("upload", "Upload") },
        { id: "review", title: _t("review", "Review") },
        { id: "processing", title: _t("processing", "Processing") },
      ],
    },
    ...(!skipFinancialStudy && !isCompany
      ? [
          {
            id: "financial",
            title: _t("financialStudy", "Financial study"),
            badge: _t("recommended", "Recommended"),
          },
        ]
      : []),
    ...(!isCompany && !isIframe
      ? [
          {
            id: "application",
            title: _t("application", "Application"),
          },
        ]
      : [
          {
            id: "completed",
            title: _t("profileCompleted", "Profile completed"),
          },
        ]),
  ];

  // Company login: KYB -> documents -> occupants.
  const companyTenantSteps = [
    {
      id: "business",
      title: _t(
        "businessVerification",
        "Onboarding sidebar step for company tenants: Didit KYB business verification"
      ),
    },
    { id: "documents", title: _t("documents", "Documents") },
    {
      id: "occupants",
      title: _t(
        "whoWillLiveHere",
        "Onboarding sidebar step for company tenants: people who will live in the property"
      ),
    },
  ];
  const steps = isCompanyTenant ? companyTenantSteps : tenantSteps;

  const showCompletedStep = isCompany || isIframe;
  const companyTenantActiveId =
    step === 2 ? "business" : step === 11 ? "occupants" : "documents";
  const requestedActiveId = isCompanyTenant
    ? companyTenantActiveId
    : step === 1
      ? "details"
      : step === 2
        ? "identity"
        : step <= 5
          ? "documents"
          : step === 6 && !skipFinancialStudy && !isCompany
            ? "financial"
            : showCompletedStep
              ? "completed"
              : "application";
  // When the financial step is hidden, fall back to the completion step.
  const fallbackId =
    requestedActiveId === "financial" && showCompletedStep
      ? "completed"
      : requestedActiveId === "application" && showCompletedStep
        ? "completed"
        : requestedActiveId;
  const activeIndexRaw = steps.findIndex(({ id }) => id === fallbackId);
  const activeIndex = activeIndexRaw === -1 ? 0 : activeIndexRaw;

  return (
    <ProgressRail
      label={_t("yourProcess", "Your process")}
      steps={steps}
      activeIndex={activeIndex}
      language={
        !hideLanguageSwitcher && (
          <LanguageSwitch
            currentLang={currentLang}
            isLangChanging={isLangChanging}
            onChange={handleLanguageChange}
          />
        )
      }
      extra={
        companyLogo && (
          <div className="onboarding-progress__company">
            <div
              className="onboarding-progress__company-mask"
              aria-hidden="true"
            />
            <img
              className="onboarding-progress__company-logo"
              src={companyLogo}
              alt="Company logo"
            />
          </div>
        )
      }
    >
      <p className="onboarding-progress__eyebrow">
        {_t("yourProcess", "Your process")}
      </p>
      <StepList
        steps={steps.map((progressStep) => ({
          ...progressStep,
          badgeTone:
            progressStep.id === "identity" && identityStillValid
              ? "valid"
              : "default",
        }))}
        activeIndex={activeIndex}
        completedLabel={_t("completed", "Completed")}
      />
    </ProgressRail>
  );
};

export default OnboardingProgress;
